/**
 * Armar una cotizacion nueva (COT-01).
 *
 * Con `?desde=<id>` nace como copia de otra: es la forma de corregir una, que
 * no se edita. La copia trae el cliente, las condiciones y los productos, pero
 * con el precio de HOY —que es el que va a cobrar el servidor—, y deja fuera
 * los productos que ya no existen.
 *
 * Al guardar se descarga el PDF y se vuelve a la lista, con la cotizacion
 * recien hecha abierta.
 */
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import type { CrearCotizacionDto } from '@bandu/compartido';

import { BarraAdmin } from '../../componentes/administracion/BarraAdmin';
import { FormularioCotizacion } from '../../componentes/administracion/FormularioCotizacion';
import type { ValoresCotizacion } from '../../componentes/administracion/FormularioCotizacion';
import { AvisoError, Cargando } from '../../componentes/comunes/EstadoPeticion';
import { Icono } from '../../componentes/comunes/Icono';
import { usePeticion } from '../../hooks/use-peticion';
import { Seo } from '../../seo/Seo';
import { obtenerProductoAdmin } from '../../servicios/administracion.servicio';
import {
  crearCotizacion,
  descargarPdfCotizacion,
  obtenerCotizacion,
} from '../../servicios/cotizaciones.servicio';
import {
  lineaDesdeProducto,
  observacionesPorDefecto,
  PARAMETRO_COTIZACION,
  VALIDEZ_POR_DEFECTO,
} from '../../utilidades/cotizacion';
import { mensajeDe } from '../../utilidades/mensaje-error';
import { rutas } from '../../utilidades/rutas';

const EN_BLANCO: ValoresCotizacion = {
  cliente: { nombre: '', rut: '', contacto: '', telefono: '', email: '' },
  lineas: [],
  descuento: '',
  validez: String(VALIDEZ_POR_DEFECTO),
  observaciones: observacionesPorDefecto(VALIDEZ_POR_DEFECTO),
};

interface Copia {
  numero: string;
  valores: ValoresCotizacion;
  /** Productos de la original que ya no estan en el catalogo. */
  omitidos: number;
}

async function copiaDe(id: number, senal: AbortSignal): Promise<Copia> {
  const original = await obtenerCotizacion(id, senal);
  const vigentes = original.items.filter((item) => item.producto !== null);

  const lineas = await Promise.all(
    vigentes.map(async (item) => {
      const producto = await obtenerProductoAdmin(item.producto?.id ?? 0, senal);
      return lineaDesdeProducto({ ...producto, imagen: producto.imagenes[0] ?? null }, item.cantidad);
    }),
  );

  const { cliente } = original;
  return {
    numero: original.numero,
    omitidos: original.items.length - vigentes.length,
    valores: {
      cliente: {
        nombre: cliente.nombre,
        rut: cliente.rut ?? '',
        contacto: cliente.contacto ?? '',
        telefono: cliente.telefono ?? '',
        email: cliente.email ?? '',
      },
      lineas,
      descuento: original.descuentoPorcentaje > 0 ? String(original.descuentoPorcentaje) : '',
      validez: String(original.validezDias),
      observaciones: original.observaciones ?? '',
    },
  };
}

function idDe(valor: string | null): number | null {
  const id = Number(valor);
  return Number.isInteger(id) && id > 0 ? id : null;
}

function sinCopia(): Promise<null> {
  return Promise.resolve(null);
}

export function CotizacionNueva() {
  const navegar = useNavigate();
  const [parametros] = useSearchParams();
  const desde = idDe(parametros.get('desde'));

  const copia = usePeticion(
    (senal) => (desde === null ? sinCopia() : copiaDe(desde, senal)),
    [desde ?? 0],
  );

  const [enviando, setEnviando] = useState(false);
  const [falla, setFalla] = useState<string | null>(null);

  async function guardar(datos: CrearCotizacionDto): Promise<void> {
    setEnviando(true);
    setFalla(null);

    let creada;
    try {
      creada = await crearCotizacion(datos);
    } catch (fallo) {
      setFalla(mensajeDe(fallo));
      setEnviando(false);
      return;
    }

    // Ya esta guardada: si la descarga falla, se reintenta desde la lista.
    await descargarPdfCotizacion(creada).catch(() => undefined);
    navegar(`${rutas.administracion.cotizaciones()}?${PARAMETRO_COTIZACION}=${creada.id}`);
  }

  const listo = desde === null || copia.datos !== null;

  return (
    <>
      <Seo
        titulo="Nueva cotizacion"
        descripcion="Armar una cotizacion para un cliente."
        ruta={rutas.administracion.cotizacionNueva()}
        sinIndexar
      />

      <BarraAdmin
        titulo="Nueva cotización"
        bajada={
          copia.datos !== null
            ? `Copia de ${copia.datos.numero}, con los precios de hoy`
            : 'Al guardarla se descarga el PDF'
        }
        acciones={
          <Link className="btn btn-sm btn-contorno" to={rutas.administracion.cotizaciones()}>
            <Icono nombre="flechaIzq" />
            Volver
          </Link>
        }
      />

      <div className="admin-cuerpo">
        {desde !== null && copia.estado === 'cargando' ? <Cargando que="la cotizacion" /> : null}
        {copia.estado === 'error' ? <AvisoError error={copia.error} /> : null}

        {copia.datos !== null && copia.datos.omitidos > 0 ? (
          <p className="acceso-error" role="status">
            <Icono nombre="alerta" />
            <span>
              {copia.datos.omitidos === 1
                ? 'Un producto de la original ya no está en el catálogo y quedó fuera.'
                : `${copia.datos.omitidos} productos de la original ya no están en el catálogo y quedaron fuera.`}
            </span>
          </p>
        ) : null}

        {listo ? (
          <FormularioCotizacion
            inicial={copia.datos?.valores ?? EN_BLANCO}
            enviando={enviando}
            falla={falla}
            onGuardar={(datos) => {
              void guardar(datos);
            }}
          />
        ) : null}
      </div>
    </>
  );
}
